import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { FlyError } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { useAxios } from "@wayflyer/auth-sdk";
import { AxiosError, isAxiosError } from "axios";
import {
  getFundingApplicationQueryOptions,
  getFinancingStageQueryKey,
  useErrorModal,
  type FormattedFundingApplicationScreen,
} from "shared-ui";

import {
  FUNDING_APPLICATION_API,
  FUNDING_APPLICATION_STATE_API,
  SUBSEQUENT_FUNDING_APPLICATION_API,
} from "../../components/constants";
import { useNavigate } from "react-router";

export {
  getFundingApplicationQueryOptions,
  useFundingApplicationMutation,
  type FormattedFundingApplicationScreen,
  type FormattedFundingApplicationScreenUpdate,
} from "shared-ui";

export const useFundingApplication = (customerDomain: string) =>
  useSuspenseQuery({
    ...getFundingApplicationQueryOptions(customerDomain),
    select: (data): FormattedFundingApplicationScreen => {
      const urls = data.websiteUrls ? data.websiteUrls.split(",") : [];
      const intendedUses = data.intendedUse ? data.intendedUse.split(",") : [];
      return { ...data, websiteUrls: urls, intendedUse: intendedUses };
    },
    refetchOnMount: "always",
  });

const getErrorStatus = (error: Error) => (isAxiosError(error) ? (error.response?.status ?? error.status) : undefined);

export const isRejectedFundingApplicationStateTransition = (error: unknown): boolean =>
  error instanceof Error && getErrorStatus(error) === 409;

export const useFundingApplicationStateMutation = (customerDomain: string) => {
  const client = useAxios();
  const navigate = useNavigate();
  const mutationUrl = FUNDING_APPLICATION_STATE_API.replace(":customerSlug", customerDomain);

  return useMutation({
    mutationKey: ["updateFundingApplicationState"],
    mutationFn: (data: FundingApplicationSchemas.CustomerFundingApplicationStateUpdate) => {
      return client.post(mutationUrl, data);
    },
    // 409 = transition rejected by the backend monotonicity guard.
    // Redirect to root so they end up where they should be.
    onError: (error: Error) => {
      if (getErrorStatus(error) === 409) {
        navigate("/");
      }
    },
    throwOnError: (error: Error) => getErrorStatus(error) !== 409,
  });
};

export const useSubmitSubsequentFundingApplication = (customerDomain: string) => {
  const client = useAxios();
  const url = SUBSEQUENT_FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);
  const { openErrorModal } = useErrorModal();

  return useMutation({
    mutationKey: ["submitSubsequentFundingApplication"],
    mutationFn: (data: FundingApplicationSchemas.SubmitSubsequentFundingApplicationIn) => client.post(url, data),
    onError: (error: AxiosError) => {
      openErrorModal({
        variant: "submit-error",
        error: new Error(`Error starting new subsequent funding application`, { cause: error }),
      });
    },
  });
};

export const useCreateFundingApplicationMutation = (
  customerDomain: string,
  customerUuid: string | null | undefined
) => {
  const client = useAxios();
  const queryClient = useQueryClient();
  const mutationUrl = FUNDING_APPLICATION_API.replace(":customerSlug", customerDomain);
  const { openErrorModal, closeErrorModal } = useErrorModal();

  return useMutation({
    mutationKey: ["createNewSalesApplication"],
    mutationFn: () => client.post<FundingApplicationSchemas.FundingApplicationCreatedSchema>(mutationUrl),
    onError: (error: AxiosError) => {
      if (error?.status === 403) {
        // occurs when staff members try to create deals from the customer app
        const flyError: FlyError = new FlyError({
          title: "Not permitted",
          message: "Staff users should create new deals using the company page or Deals page in the staff application",
          error: new Error("Error starting new funding application", { cause: error }),
          primaryAction: {
            content: "Close",
            onAction: closeErrorModal,
          },
        });
        openErrorModal(flyError as FlyError);
      } else {
        openErrorModal({
          variant: "submit-error",
          error: new Error("Error starting new funding application", { cause: error }),
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getFundingApplicationQueryOptions(customerDomain).queryKey });
      await queryClient.invalidateQueries({ queryKey: getFinancingStageQueryKey(customerUuid) });
    },
  });
};
